import type { ContextProfileKind } from '@fos/shared/contracts/context';
import { CONTEXT_PROFILE_FILES } from '@fos/shared/contracts/context';
import { History } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { formatDateTimeShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Markdown } from '../../../ui/Markdown';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { DiffView } from '../../knowledge/components/DiffView';

/** Historia wersji profilu OWNER.md / BUSINESS.md: autor, data, opis zmiany, diff względem poprzedniej wersji. */
export function ProfileHistoryDialog({
  kind,
  open,
  onOpenChange,
}: {
  kind: ContextProfileKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const versions = trpc.context.listVersions.useQuery({ kind }, { enabled: open });
  const [selected, setSelected] = useState<number | null>(null);
  const [view, setView] = useState<'diff' | 'content'>('diff');

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setView('diff');
      return;
    }
    if (selected === null && versions.data?.[0]) setSelected(versions.data[0].versionNo);
  }, [open, versions.data, selected]);

  const version = trpc.context.getVersion.useQuery(
    { kind, versionNo: selected ?? 1 },
    { enabled: open && selected !== null },
  );

  let body: ReactNode;
  if (versions.isPending) {
    body = (
      <div role="status" aria-label="Wczytywanie" className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  } else if (versions.isError) {
    body = <ErrorState compact onRetry={() => void versions.refetch()} retrying={versions.isFetching} />;
  } else if (versions.data.length === 0) {
    body = <EmptyState compact icon={History} title="Profil nie ma jeszcze wersji" />;
  } else {
    const v = version.data;
    body = (
      <div className="flex flex-col gap-4">
        <Field label="Wersja">
          <Select
            value={selected !== null ? String(selected) : undefined}
            onValueChange={(next) => setSelected(Number(next))}
            options={versions.data.map((item) => ({
              value: String(item.versionNo),
              label: `Wersja ${item.versionNo}${item.current ? ' · bieżąca' : ''} · ${formatDateTimeShort(item.createdAt)} · +${item.added} / −${item.removed}`,
            }))}
          />
        </Field>
        {v && (
          <p className="text-caption text-fg-muted">
            {v.author ? `${v.author.name} · ` : ''}
            {formatDateTimeShort(v.createdAt)}
            {v.changeNote ? ` · ${v.changeNote}` : ''}
          </p>
        )}
        <div className="flex justify-end">
          <Segmented
            aria-label="Widok wersji"
            size="s"
            value={view}
            onValueChange={setView}
            options={[
              { value: 'diff', label: 'Zmiany' },
              { value: 'content', label: 'Treść' },
            ]}
          />
        </div>
        <div className="max-h-[45dvh] overflow-y-auto">
          {version.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : version.isError ? (
            <ErrorState compact onRetry={() => void version.refetch()} retrying={version.isFetching} />
          ) : v && view === 'diff' ? (
            <DiffView lines={v.diff} emptyText="Bez zmian względem poprzedniej wersji." />
          ) : v ? (
            <div className="rounded-lg border border-line p-4">
              <Markdown className="text-body-s">{v.bodyMd}</Markdown>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title={`Historia ${CONTEXT_PROFILE_FILES[kind]}`}
      description="Każdy zapis to nowa wersja. Zmiany pokazane względem poprzedniej wersji."
      footer={
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Zamknij
        </Button>
      }
    >
      {body}
    </Dialog>
  );
}
